import { ActionIcon, Group, Stack, Text, Tooltip } from "@mantine/core";
import { useParams } from "@tanstack/react-router";
import { Archive, Image as ImageIcon, Layers } from "lucide-react";
import { useState } from "react";

import { GlassPanel } from "../../../ui/GlassPanel";
import { SourceTool } from "../../../ui/SourceTool";
import classes from "../composer/composer.module.css";
import { PromptBox } from "../composer/PromptBox";
import { ModelChoice, OutputsChoice, RatioChoice, TypeChoice } from "../composer/Settings";
import { ComposerErrors, PriceAndStart } from "../composer/Start";
import { useComposer, type ComposerProps } from "../composer/use-composer";
import { defaultModel } from "../draft";
import type { DraftShape } from "../draft-state";
import { advancedModels } from "./advanced";
import { AdvancedComposer } from "./AdvancedComposer";
import { AdvancedSwitch } from "./AdvancedMode";
import image from "./image.module.css";
import { referenceCap, referencesLeft } from "./references";
import type { AdvancedCarry } from "./use-advanced";
import { UploadState } from "../../../platform/media/image-upload";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import { LayerizeComposer, layerizeModel } from "./LayerizeComposer";
import { ComposerTools } from "./ImageTools";
import { PromptTemplates } from "./PromptTemplates";
import { SourceThumb } from "./SourceThumb";
import { useReferenceDrop } from "./useReferenceDrop";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId";

/** The simple draft's shape is not the page's while the Advanced mode reports its own. */
const UNSEEN = (_shape: DraftShape) => undefined;

/**
 * The image studio's floating composer (design-system.md 3.3): the Advanced switch on top, the
 * prompt with the chosen reference under it, the source tools at the footer's left, and the
 * settings, the price and the start at its right. The switch turns it into live's Advanced mode
 * (#234), a form of its own, as live's are two.
 */
export function ImageComposer(props: ComposerProps) {
  // What the Advanced mode started from while it is on, else null. The library's Clone opens it
  // on: its image the one reference, the default model and its ratio, no product (live's
  // `useImagesGenerationFormValues` for a clone).
  const [advanced, setAdvanced] = useState<AdvancedCarry | null>(() =>
    props.initialAdvanced === undefined || advancedModels(props.catalog).length === 0
      ? null
      : {
          product: null,
          references: [props.initialAdvanced],
          modelId: defaultModel(props.catalog.image_models).id,
          aspectRatio: null,
        },
  );
  const { teamId, brandId } = useParams({ from: ROUTE });
  // Live's Layerize (G13): its picker while open, then its mode while a picture is chosen; the
  // library's Layerize opens it on its image (live's `useLayerizeRouteSource`).
  const [choosingLayerize, setChoosingLayerize] = useState(false);
  const [layerizing, setLayerizing] = useState<PickedImage | null>(props.initialLayerize ?? null);
  const c = useComposer({
    ...props,
    onShapeChange: advanced === null && layerizing === null ? props.onShapeChange : UNSEEN,
  });
  const layerize = layerizeModel(c.catalog.image_models);
  const drop = useReferenceDrop({
    addReference: c.addReference,
    room: c.referencesRoom - c.references.length,
    // Live's Tweak form takes no files (#678).
    canTake: advanced === null && c.canSource && !c.tweak && !c.start.isPending,
  });
  if (layerize !== undefined && layerizing !== null) {
    return (
      <LayerizeComposer
        model={layerize}
        picture={layerizing}
        onSwitchOff={() => setLayerizing(null)}
        onShapeChange={props.onShapeChange}
        onStarted={props.onStarted}
      />
    );
  }
  if (advanced !== null) {
    return (
      <AdvancedComposer
        catalog={c.catalog}
        carry={advanced}
        onShapeChange={props.onShapeChange}
        onStarted={props.onStarted}
        onSwitchOff={(back) => {
          // Live's switch back keeps the product, the references the model has room for, the
          // model and, without a source, the ratio; the prompt starts afresh.
          const model = c.catalog.image_models.find((each) => each.id === back.modelId);
          if (model !== undefined) c.chooseModel(model);
          const holder = model ?? c.model;
          const source =
            back.product === null ? null : { kind: "asset" as const, asset_id: back.product.assetId };
          c.chooseProduct(back.product);
          c.chooseReferences(back.references.slice(0, referencesLeft(referenceCap(holder), source)));
          const framed = back.product !== null || back.references.length > 0;
          c.set({
            prompt: "",
            ...(framed || back.aspectRatio === null ? {} : { aspectRatio: back.aspectRatio }),
          });
          setAdvanced(null);
        }}
      />
    );
  }
  // Live's slot budget: at the cap, a tool that would add an image greys out.
  const full = c.references.length >= c.referencesRoom;
  const products = c.canSource
    ? c.products((wrap) => (
        <SourceTool
          label="Products"
          icon={<Archive size={20} strokeWidth={1.5} />}
          view={c.product}
          removeLabel="Remove the product"
          onRemove={c.removeSource}
          onClick={() => c.toggleProducts()}
          disabled={c.product === null && full}
          open={c.productsOpen}
          target={wrap}
        />
      ))
    : undefined;
  const references = c.canSource ? (
    <SourceTool
      label="Reference images"
      icon={<ImageIcon size={20} strokeWidth={1.5} />}
      view={null}
      removeLabel="Remove the source image"
      onRemove={c.removeSource}
      onClick={c.openReferences}
      disabled={full}
    />
  ) : undefined;
  return (
    <GlassPanel
      collapsed={c.collapsed}
      onCollapsedChange={c.setCollapsed}
      drop={drop}
      header={
        <AdvancedSwitch
          on={false}
          // Live's Advanced list is the models that make ad creatives. A tweak's Advanced is
          // live's advanced Tweak (an ad structure's old and new values), which ours lacks (#678).
          disabled={c.tweak || advancedModels(c.catalog).length === 0}
          onChange={() =>
            setAdvanced({
              product: c.product,
              references: c.references,
              modelId: c.model.id,
              aspectRatio: c.draft.aspectRatio,
            })
          }
        />
      }
      summary={<span className={classes.summary}>{c.summary}</span>}
      above={undefined}
      footer={
        // The rows wrap where the window is too narrow for one: the model, ratio and count under the
        // tools, the start beside them (spec ui-perf/classic-create-widths).
        <Group justify="space-between" gap={12}>
          <Group gap={12}>
            {/* Live's Tweak form has none of the tools (issue 678). */}
            {c.tweak ? null : (
              <ComposerTools
                products={products}
                references={references}
                templates={
                  <PromptTemplates
                    draft={{
                      prompt: c.draft.prompt,
                      referenceIds: c.references.map((each) => each.assetId),
                    }}
                    onApply={c.applyTemplate}
                  />
                }
                layerize={
                  layerize === undefined ? undefined : (
                    <Tooltip label="Layerize image">
                      <ActionIcon
                        variant="subtle"
                        size={36}
                        aria-label="Layerize image"
                        onClick={() => setChoosingLayerize(true)}
                      >
                        <Layers size={20} strokeWidth={1.5} />
                      </ActionIcon>
                    </Tooltip>
                  )
                }
              />
            )}
          </Group>
          <Group gap={12}>
            <TypeChoice c={c} />
            <ModelChoice c={c} />
            <RatioChoice c={c} />
            <OutputsChoice c={c} />
            <PriceAndStart c={c} />
          </Group>
        </Group>
      }
    >
      <Stack gap={4}>
        {c.from}
        <PromptBox c={c} />
        {c.reference === null && c.references.length === 0 ? null : (
          <div className={image.thumbs}>
            {c.reference === null ? null : (
              <SourceThumb
                view={c.reference}
                removeLabel="Remove the source image"
                onRemove={c.removeSource}
              />
            )}
            {c.references.map((each) => (
              <SourceThumb
                key={each.assetId}
                view={each}
                removeLabel={`Remove ${each.name}`}
                onRemove={() => c.removeReference(each.assetId)}
              />
            ))}
          </div>
        )}
        <UploadState upload={drop.upload} />
        {drop.problem === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {drop.problem}
          </Text>
        )}
        <ComposerErrors c={c} />
      </Stack>
      {c.dialog}
      {choosingLayerize ? (
        <ReferencesDialog
          teamId={teamId}
          brandId={brandId}
          title="Choose image to Layerize"
          subtitle="Choose from your brand Library or upload a new image"
          max={1}
          chosen={[]}
          onClose={() => setChoosingLayerize(false)}
          onConfirm={(picked) => {
            setChoosingLayerize(false);
            setLayerizing(picked[0] ?? null);
          }}
        />
      ) : null}
    </GlassPanel>
  );
}
